import { useState } from 'react'
import styles from './AddVersionModal.module.css'
import Icon from './Icon'
import { ACCEPTED_DOCUMENT_EXTENSIONS, productVersionLabel, SENTINEL_PRODUCT_VERSION } from './sourceVisuals'

function UploadIcon() {
  return <Icon name="upload" />
}

/**
 * AddVersionModal — add a content-set to a document that already exists, for a
 * product version it doesn't cover yet. Re-using a version replaces that
 * version's content and leaves the document's other versions alone, so the
 * form says so as soon as the typed version matches an existing one.
 *
 * The caller mounts this only while a document is targeted, so the form starts
 * empty every time it opens with no reset effect to get wrong — which matters
 * because the page re-derives `source` on every re-list, and resetting on that
 * would drop the file the user just chose whenever a version's status changed.
 *
 * @param {object} props
 * @param {object} props.source — the document gaining a version
 * @param {function(): void} props.onClose
 * @param {function({ productVersion: string, file: File }): Promise<void>} props.onSubmit
 */
export default function AddVersionModal({ source, onClose, onSubmit }) {
  const [productVersion, setProductVersion] = useState('')
  const [file, setFile] = useState(/** @type {File|null} */ (null))
  const [dragActive, setDragActive] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(/** @type {string|null} */ (null))

  const existingVersions = (source.contentSets ?? []).map(cs => cs.productVersion)
  // An empty field means version-agnostic, which is a real version-slot of its
  // own — so it can collide with an existing content-set just like a typed one.
  const replacesExisting = existingVersions.includes(productVersion.trim() || SENTINEL_PRODUCT_VERSION)

  function handleDrop(e) {
    e.preventDefault()
    setDragActive(false)
    const dropped = e.dataTransfer.files?.[0]
    if (dropped) setFile(dropped)
  }

  async function handleSubmit(e) {
    e.preventDefault()

    if (!file) {
      setError('Choose a file to upload.')
      return
    }
    if (!ACCEPTED_DOCUMENT_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))) {
      // Soft client-side gate — the real enforcement is the S3 upload policy's
      // Content-Type condition.
      setError('Unsupported file type.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      await onSubmit({ productVersion: productVersion.trim(), file })
      onClose()
    } catch (err) {
      setError(err.message || 'Failed to add the version.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.backdrop}>
      <div className={styles.modal}>

        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>Add Version</h2>
            <p className={styles.subtitle}>{source.name}</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close modal">
            <Icon name="close" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.body}>

            {error && <div className={styles.errorNotice}>{error}</div>}

            <div className={styles.field}>
              <label className={styles.label} htmlFor="avm-product-version">
                Product Version <span className={styles.optional}>(leave blank for all versions)</span>
              </label>
              <input
                id="avm-product-version"
                className={styles.input}
                type="text"
                placeholder="e.g. 25.1"
                value={productVersion}
                onChange={e => setProductVersion(e.target.value)}
              />
              {existingVersions.length > 0 && (
                <span className={styles.hint}>
                  Already covered: {existingVersions.map(productVersionLabel).join(', ')}
                </span>
              )}
              {replacesExisting && (
                <span className={styles.warning}>
                  This replaces the content already indexed{' '}
                  {productVersion.trim()
                    ? `for version ${productVersion.trim()}`
                    : `under "${SENTINEL_PRODUCT_VERSION}", which applies to all versions`}.
                  Other versions of this document are untouched.
                </span>
              )}
            </div>

            <div className={styles.field}>
              <label
                htmlFor="avm-file"
                className={`${styles.dropzone} ${dragActive ? styles.dropzoneActive : ''}`}
                onDragOver={e => { e.preventDefault(); setDragActive(true) }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
              >
                <UploadIcon />
                {file ? (
                  <span className={styles.dropzoneFile}>{file.name}</span>
                ) : (
                  <>
                    <span className={styles.dropzoneTitle}>Drag &amp; drop a document</span>
                    <span className={styles.dropzoneSub}>or click to browse your files</span>
                  </>
                )}
              </label>
              <input
                id="avm-file"
                className={styles.hiddenFileInput}
                type="file"
                accept={ACCEPTED_DOCUMENT_EXTENSIONS.join(',')}
                onChange={e => setFile(e.target.files?.[0] ?? null)}
              />
            </div>

          </div>

          <div className={styles.footer}>
            <button
              type="button"
              className={`${styles.footerBtn} ${styles.cancelBtn}`}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${styles.footerBtn} ${styles.submitBtn}`}
              disabled={submitting}
            >
              {submitting ? 'Uploading…' : 'Add Version'}
            </button>
          </div>
        </form>

      </div>
    </div>
  )
}
